import { useHistory } from 'react-router-dom'
import { useTranslation } from 'contexts/Localization'
import { CloseButton } from 'components/CloseButton'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { Text, Link, Box } from 'vvs-uikit'
import Collapse from 'components/Collapse/Base'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { Path } from 'config/constants/paths'

import { useEffect, useRef } from 'react'
import { LightPageContainer } from '../styles'
import {
  Banner,
  BannerContent,
  CloseBtnWrapper,
  FaqContent,
  SectionTitle,
  QuestionText,
  AnswerUl,
  Hr,
  AnswerSubUl,
} from './styles'

const TeamFAQ: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory<{ focus?: string }>()

  const feedingRef = useRef<HTMLDivElement>()
  const cleaningRef = useRef<HTMLDivElement>()
  const teamHealthRef = useRef<HTMLDivElement>()

  const focusOnFeeding = history.location.state?.focus === 'feeding'
  const focusOnCleaning = history.location.state?.focus === 'cleaning'
  const focusOnTeamHealth = history.location.state?.focus === 'teamHealth'

  useEffect(() => {
    if (focusOnFeeding && feedingRef.current) {
      const timeout = setTimeout(() => {
        feedingRef.current.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timeout)
    }
    if (focusOnCleaning && cleaningRef.current) {
      const timeout = setTimeout(() => {
        cleaningRef.current.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timeout)
    }
    if (focusOnTeamHealth && teamHealthRef.current) {
      const timeout = setTimeout(() => {
        teamHealthRef.current.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timeout)
    }
    return null
  }, [focusOnFeeding, focusOnCleaning, focusOnTeamHealth])

  return (
    <LightPageContainer>
      <Banner bgImg="/images/vvsGotchi/bg_pink.png" minHeight="118px">
        <BannerContent>
          <PixelatedImage src="/images/vvsGotchi/mole_team.png" alt="" width="201px" />
          <CloseBtnWrapper>
            <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
          </CloseBtnWrapper>
        </BannerContent>
      </Banner>
      <FaqContent>
        <SectionTitle as="h1">{t('Team FAQ')}</SectionTitle>

        <Collapse title={<QuestionText>{t('What is the Team page for?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>
              {t(
                'Welcome to the Team page! Here, you can find out more detailed information about your Miner Moles, including their EXP earned, health score and farm boost multiplier',
              )}
            </li>
            <li>
              {t(
                'On this page, you can 1) add Miner Moles to your Team, 2) feed your Moles, and 3) clean your Moles when they get dirty',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('Why I need to put any Moles in Team?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Only Miner Moles on your Team can participate in Quests to earn EXP')}</li>
            <li>{t('EXP is what determines your Moles NFT farm boost')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('I only have 1 Miner Mole, do I still need to create a team?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Yes! Only Moles on the Team can participate in Quests.')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('How many Moles can I add to my Team?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('You can add a maximum of 5 Moles to your Team')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('What happens to the other Moles not on the Expedition Team?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'Unfortunately, these Moles will have to sit on the sidelines - they will be excluded from VVSGotchi activities such as Quest expeditions, and will also not earn any EXP :(',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('How often can I change my team members?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'When you update your team members, there will be a 72 hour locking period. This means that you will be unable to remove any of your team members until the locking period ends. ',
              )}
            </li>
            <li>{t('You can refer to our docs for more details on our locking period')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('Do you have a step-by-step guide on how to form a Team?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t('Yes! Click ')}
              <Link
                inline
                underline="always"
                bold
                href="https://docs.vvs.finance/product-guides/vvs-gotchi/team-page"
                target="_blank"
              >
                {t('here')}
              </Link>
              {t(' for a guide on how to do this')}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />

        <SectionTitle as="h1">{t('About Health Meter')}</SectionTitle>

        <Collapse title={<QuestionText>{t('What is the Team Health Meter?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>
              {t(
                'The Team Health Meter is the average of your Team’s health, rounded down to the nearest whole number',
              )}
            </li>
            <li>
              {t(
                'Going on quests is exhausting, and this impacts the Team’s health. The health score will decline by 1 point every day',
              )}
            </li>
            <li>
              {t(
                'You need to give your Team adequate TLC (by feeding and cleaning them) in order to prevent their health from declining!',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          ref={teamHealthRef}
          initIsExpand={focusOnTeamHealth}
          title={<QuestionText>{t('What is the Team Health Meter for?')}</QuestionText>}
          arrowColor="black"
          contentWrapperProps={{ flexDirection: 'column' }}
        >
          <AnswerUl>
            <li>{t('Your team’s health meter score determines the amount of EXP boost earned per quest')}</li>
            <li>{t('The healthier your Team, the higher the EXP boost, and vice versa')}</li>
            <li>{t('Here’s the breakdown of EXP boosts based on health levels')}</li>
          </AnswerUl>

          <Box mb={3}>
            <img src="/images/vvsGotchi/health_meter_levels.png" alt={t('health meter levels')} />
          </Box>

          <AnswerUl>
            <li>{t('Excellent: 100')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: + 50%')}</li>
            </AnswerSubUl>
            <li>{t('Healthy: 71 - 99')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: + 35% more')}</li>
            </AnswerSubUl>
            <li>{t('Normal: 61 - 70')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: 0%')}</li>
            </AnswerSubUl>
            <li>{t('Unhealthy: 11 - 60')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: - 35%')}</li>
            </AnswerSubUl>
            <li>{t('Sick: Below 10')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: - 50%')}</li>
            </AnswerSubUl>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={
            <QuestionText>
              {t('Why is my Mole’s health score at 70 when I first added it to my Expedition Team?')}
            </QuestionText>
          }
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'If this is the first time adding a brand new Mole to your Team, the Mole’s health will start off at a default score of 70',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={
            <QuestionText>
              {t('Why is my Mole’s health score different from 70 when I added it to my Expedition Team?')}
            </QuestionText>
          }
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'If you 1) purchased a Mole from another user or 2) transferred it from another wallet address, that Mole’s current health score will be saved and transferred over to you',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('What is the maximum health score?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('The maximum health score per Mole is 100')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={
            <QuestionText>{t('Will the health of the Moles not on the Expedition Team get affected?')}</QuestionText>
          }
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'No, the health of any Mole not on the Expedition Team will remain dormant until you add them onto the team',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />

        <SectionTitle as="h1" ref={feedingRef}>
          {t('About Feeding')}
        </SectionTitle>

        <Collapse title={<QuestionText>{t('Why are there different kinds of food?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Different types of food will give your Moles different amounts of health points')}</li>
            <li>{t('You can only feed each Mole once a day, so choose wisely!')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={
            <QuestionText>{t('I fed my Mole(s) broccoli - why didn’t the Team health meter increase?')}</QuestionText>
          }
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('While broccoli is nutritious, Miner Moles are not fond of vegetables')}</li>
            <li>{t('Your Moles are more likely to reject broccoli than to eat it')}</li>
            <li>{t('If your Team has rejected eating the broccoli, you will not get any health points')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('Can I feed each of my Moles individually?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Yes you can!')}</li>
            <li>
              {t('You can feed each Mole on your team separately, or feed them all at once - it’s totally up to you!')}
            </li>
            <li>{t('Do note that each Mole only eats once a day, so choose your food item wisely!')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('Can I feed Moles that are not on my Team?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Unfortunately no, you will need to add the Mole to your Team before you can feed it')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('At what time does the health of my Team decline every day?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('The health of your Team declines by 1 point at random time once a day')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('My team’s health meter is 100. Why am I unable to feed them?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('Congrats on the excellent health of your Moles!')}</li>
            <li>{t('When your team’s health meter hits 100, it means that your Moles are all full')}</li>
            <li>{t('Come back later to feed them when they get hungry again (when health meter declines to 99)')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />

        <SectionTitle as="h1" ref={cleaningRef}>
          {t('About Cleaning')}
        </SectionTitle>

        <Collapse title={<QuestionText>{t('When do I need to clean my Moles?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('You only need to clean your Team when their status changes from “clean” to “dirty”')}</li>
            <li>
              {t(
                'When the status changes, you will need to clean them within 8 hours or your team will lose health points',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('When does the status change from “clean” to “dirty”?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>{t('This status changes at random time once a day')}</li>
            <li>{t('This means that you will need to keep a lookout for when this happens!')}</li>
            <li>
              {t(
                'Don’t forget to clean them as soon as possible - If you’re unable to clean them within 8 hours of them becoming dirty, you will lose 2 health points',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('What happens if I don’t clean my Moles?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'You will continue to lose 2 health points every 8 hours that your Team is not tended to - so be sure to clean them ASAP!',
              )}
            </li>
          </AnswerUl>
        </Collapse>

        <Text color="black" fontSize="13px" mt="100px" mb="40px">
          {t('For more information, find it ')} {/* TBD: VVS Guide Book href */}
          <Link
            inline
            underline="always"
            bold
            href="https://docs.vvs.finance/product-guides/vvs-gotchi/team-page"
            target="_blank"
          >
            {t('here')}
          </Link>
        </Text>
      </FaqContent>
    </LightPageContainer>
  )
}

export default TeamFAQ
